/**
 * Carte au centre de la séance (design system, CarteDeRevision). Basique : question au recto,
 * question et réponse au verso. Texte à trous et masquages : un seul contenu, qui se révèle
 * au verso (trous, zones ou cases demandés). Le cadre garde la même taille au recto et au
 * verso : la question ne bouge pas, la réponse s'ajoute dessous.
 */
import { useEffect, useMemo, useRef } from 'react';
import type { CarteMere } from '../../cartes/carte-mere';
import { analyseurSansTrous } from '../../contenu/syntaxe';
import { ContenuDeCarte, rendreEnLigneMarkdown } from '../contenu/ContenuDeCarte';
import { MasquageImage, MasquageTableau } from './Masquages';

/** La question tient-elle en un paragraphe (rendue dans le titre de l'écran) ? */
function questionSimple(markdown: string): boolean {
  const premier = analyseurSansTrous.parse(markdown).topNode.firstChild;
  return premier !== null && premier.name === 'Paragraph' && premier.nextSibling === null;
}

function Question({ markdown }: { markdown: string }) {
  const simple = useMemo(() => questionSimple(markdown), [markdown]);
  if (simple)
    return <h1 className="fc-carte-de-revision__question">{rendreEnLigneMarkdown(markdown)}</h1>;
  return (
    <div
      className="fc-carte-de-revision__question fc-carte-de-revision__question--longue"
      role="heading"
      aria-level={1}
    >
      <ContenuDeCarte markdown={markdown} />
    </div>
  );
}

const NOMS_DES_TYPES = {
  trous: 'Texte à trous',
  image: 'Masquage d’image',
  tableau: 'Masquage de tableau',
} as const;

export interface CarteDeRevisionProps {
  carte: CarteMere;
  /** Clé de la carte fille : recto, verso, trou:<n>, zone:<id>, case:<id> ou tout. */
  cle: string;
  face: 'recto' | 'verso';
  /** Gardés pour la compatibilité : « Afficher la réponse » est dans BarreDeNotation. */
  raccourciRetourner?: string;
  onRetourner?: () => void;
}

export function CarteDeRevision({ carte, cle, face }: CarteDeRevisionProps) {
  const reponse = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (face === 'verso') reponse.current?.focus({ preventScroll: true });
  }, [face, cle]);

  if (carte.type === 'basique') {
    const [question, texteReponse] =
      cle === 'verso'
        ? [carte.contenu.verso, carte.contenu.recto]
        : [carte.contenu.recto, carte.contenu.verso];
    return (
      <article aria-label="Carte en cours" className="fc-carte-de-revision" data-face={face}>
        <Question markdown={question} />
        <div className="fc-carte-de-revision__reponse" tabIndex={-1} ref={reponse}>
          {face === 'verso' && <ContenuDeCarte markdown={texteReponse} />}
        </div>
      </article>
    );
  }

  const demandee = cle === 'tout' ? null : cle.slice(cle.indexOf(':') + 1);
  let contenu;
  switch (carte.type) {
    case 'trous':
      contenu = (
        <ContenuDeCarte
          markdown={carte.contenu.texte}
          trous={{ face, demande: demandee === null ? 'tous' : Number(demandee) }}
        />
      );
      break;
    case 'image':
      contenu = (
        <MasquageImage
          contenu={carte.contenu}
          mode={carte.mode}
          face={face}
          zoneDemandee={demandee}
        />
      );
      break;
    case 'tableau':
      contenu = (
        <MasquageTableau
          contenu={carte.contenu}
          mode={carte.mode}
          face={face}
          caseDemandee={demandee}
        />
      );
      break;
  }
  return (
    <article
      aria-label="Carte en cours"
      className="fc-carte-de-revision fc-carte-de-revision--integree"
      data-face={face}
    >
      <h1 className="fc-sr">{NOMS_DES_TYPES[carte.type]}</h1>
      <div className="fc-carte-de-revision__integre" tabIndex={-1} ref={reponse}>
        {contenu}
      </div>
    </article>
  );
}
